import { Fragment } from 'react'
import { GetStaticPaths, GetStaticPropsContext, NextPage } from 'next'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import ROUTES from '~/shared/constants/routes.const'

import ArticleDetailComponent from '~/shared/components/Articles/ArticleDetail/ArticleDetail'
import Metatags from '~/components/Metadata/Metatags'
import { localePathsFromSiblings } from '~/components/Metadata/hreflang.util'
import { ArticleDetail } from '~/shared/components/Articles/ArticlesList/ArticlesList.types'
import { DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import { DefaultLayout } from '~/shared/layouts/DefaultLayout'
import { useGetBlogPostDetailQuery, getBlogPostDetail, getRunningQueriesThunk } from '~/redux/apis/blog.api'
import { usePageTitle } from '~/utils/tags'
import BlogSchema from '~/components/Metadata/Schema/BlogSchema'
import { BackButton } from '~/components/Buttons/BackButton'
import { GetProps } from '~/utils/pageWrapper/types'
import { getStaticPropsWrapper } from '~/utils/pageWrapper/staticPageWrapper'
import { AppLanguage } from '~/types/locale'

const TutorOnDemandBlogDetailPage: NextPage = () => {
  const { locale, query, isFallback } = useRouter()
  const { t } = useTranslation('blog')
  const { data } = useGetBlogPostDetailQuery(
    { language: locale as string, slug: query.slug as string },
    { skip: isFallback },
  )

  const blogPost = data as ArticleDetail
  const title = usePageTitle(blogPost.title)

  return (
    <Fragment>
      <Metatags
        contentType="article"
        image={blogPost.landscapeImage}
        localePaths={localePathsFromSiblings(`${ROUTES.TUTOR_ON_DEMAND_BLOG}/`, blogPost.localeSlugs ?? [])}
        metaDescription={blogPost.metaDescription}
        title={title}
      />
      <BlogSchema blogPost={blogPost} />
      <DefaultLayout>
        <BackButton href={ROUTES.TUTOR_ON_DEMAND_BLOG} pageTitle={t('title')} />

        <ArticleDetailComponent isTutorOnDemandArticle {...blogPost} enableTODProductCard={locale === AppLanguage.NO} />
      </DefaultLayout>
    </Fragment>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getProps: GetProps =
  (store) =>
  async ({ locale, params }: GetStaticPropsContext<{ slug: string }>) => {
    const slug = params?.slug as string

    store.dispatch(
      getBlogPostDetail.initiate({
        language: locale as string,
        slug,
      }),
    )

    await Promise.all(store.dispatch(getRunningQueriesThunk()))

    const queries = store.getState().blogApi.queries
    const query = Object.values(queries).find((q) => q?.endpointName === 'getBlogPostDetail')
    // `query.data` can be null when the fetch resolves empty (e.g. an unknown/invalid slug), so
    // default to `{}` to keep the `Object.keys(...)` 404 check below from throwing on null.
    const data = (query?.data ?? {}) as Record<string, unknown>

    if (Object.keys(data).length === 0) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        path: `/tutor-on-demand/blog/${slug}`,
        namespaces: ['blog', 'tutorOnDemand', ...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES],
      },
      revalidate: process.env.ENVIRONMENT === 'staging' ? 30 : false,
    }
  }
export const getStaticProps = getStaticPropsWrapper(getProps)

export default TutorOnDemandBlogDetailPage
